import { ReactNode, useEffect, useId, useState } from 'react';

import Button from 'components/ui/button';
import InfoTooltip from 'components/ui/info-tooltip';
import { getKPIPublicHistoryByTime } from 'services/dashboard.service';
import { DestaqueDoFunci, getDestaqueDoFunci } from 'services/destaques.service';
import { FormatNumber } from 'utils/helpers';
import { sanitizeHtml } from 'utils/sanitizeHtml';
import { BehaviorLabel } from 'utils/strings';

import { dataCurta } from '../data-curta';
import { corDoCartao, nomeExibido } from '../destaques-modelo';
import AvatarDoFunci from './AvatarDoFunci';
import ReconhecimentoRecebido, { ReconhecimentoDoExtrato } from './ReconhecimentoRecebido';
import RegrasDosDestaquesDaCultura from './RegrasDosDestaquesDaCultura';
import SecaoDoSelo from './SecaoDoSelo';

/** Reconhecimentos que aparecem antes do "Ver todos" (DST-25). */
const RECONHECIMENTOS_VISIVEIS = 4;

interface PerfilNosDestaquesProps {
  ciclo: { id: string; titulo: string; inicio?: unknown; fim?: unknown };
  matricula: string;
  onVoltar: () => void;
  /** Avisa a página, que põe o nome no cabeçalho ("Destaques | <nome>"). Estável (`useCallback`): entra na carga. */
  onCarregado?: (funci: DestaqueDoFunci) => void;
}

type Carga =
  | { estado: 'carregando' }
  | { estado: 'erro' }
  | { estado: 'nao-encontrado' }
  | { estado: 'pronto'; funci: DestaqueDoFunci; reconhecimentos: ReconhecimentoDoExtrato[] };

/**
 * Perfil de um funci em Destaques (`Galeria.dc.html`, DST-21 a DST-27): o cartão com quem ele é e uma seção por
 * selo conquistado no ciclo, com o que levou a cada um.
 */
export default function PerfilNosDestaques({ ciclo, matricula, onVoltar, onCarregado }: PerfilNosDestaquesProps) {
  const [carga, setCarga] = useState<Carga>({ estado: 'carregando' });
  const [tentativa, setTentativa] = useState(0);

  useEffect(() => {
    let atual = true;
    setCarga({ estado: 'carregando' });

    async function carregar() {
      const funci = await getDestaqueDoFunci(ciclo.id, matricula);
      if (!funci) return { estado: 'nao-encontrado' as const };

      // Os reconhecimentos só servem às seções da Galeria e da Cultura.
      const extrato: ReconhecimentoDoExtrato[] =
        funci.galeria || funci.cultura ? await getKPIPublicHistoryByTime(matricula, ciclo.inicio, ciclo.fim, ciclo.id) : [];
      return { estado: 'pronto' as const, funci, reconhecimentos: extrato.filter(item => !item.cancelled) };
    }

    carregar()
      .then(resultado => {
        if (!atual) return;
        setCarga(resultado);
        if (resultado.estado === 'pronto') onCarregado?.(resultado.funci);
      })
      .catch(() => {
        if (atual) setCarga({ estado: 'erro' });
      });

    return () => {
      atual = false;
    };
  }, [ciclo.id, ciclo.inicio, ciclo.fim, matricula, tentativa, onCarregado]);

  if (carga.estado === 'carregando') {
    return (
      <div>
        <span role="status" className="sr-only">
          Carregando o perfil
        </span>
        <div aria-hidden="true" className="flex flex-col gap-[22px]">
          <div className="h-[152px] animate-pulse rounded-[20px] bg-surface-200 motion-reduce:animate-none" />
          <div className="h-[260px] animate-pulse rounded-3xl bg-surface-200 motion-reduce:animate-none" />
        </div>
      </div>
    );
  }

  if (carga.estado === 'erro') {
    return (
      <Aviso texto="Não foi possível carregar o perfil.">
        <Button variant="secundario" size="sm" onClick={() => setTentativa(anterior => anterior + 1)}>
          Tentar de novo
        </Button>
      </Aviso>
    );
  }

  if (carga.estado === 'nao-encontrado') {
    return (
      <Aviso texto="Funci não encontrado nos destaques deste ciclo.">
        <Button variant="secundario" size="sm" onClick={onVoltar}>
          Voltar para Destaques
        </Button>
      </Aviso>
    );
  }

  const { funci, reconhecimentos } = carga;
  const nome = nomeExibido(funci);
  const daCultura = reconhecimentos.filter(item => item.behavior === funci.cultura);
  const comportamento = funci.cultura ? (BehaviorLabel[funci.cultura] ?? funci.cultura) : '';

  return (
    <div className="flex flex-col gap-[22px]">
      <section className="flex flex-wrap items-center gap-6 rounded-[20px] bg-surface-0 px-8 py-7 shadow-jj-01">
        <span
          aria-hidden="true"
          className={`h-24 w-24 shrink-0 overflow-hidden rounded-jj-full border-4 border-solid border-brand-blue shadow-jj-01 ${corDoCartao(funci.qtdTipos)}`}
        >
          <AvatarDoFunci funci={funci} tamanhoDaInicial="text-[32px]" />
        </span>
        <div className="flex min-w-[220px] flex-1 flex-col gap-[5px]">
          <span className="self-start rounded-jj-pill bg-brand-blue px-[11px] py-1 font-display text-[11px] font-bold uppercase tracking-[0.05em] text-brand-white">
            Ciclo {ciclo.titulo}
          </span>
          <h2 className="m-0 mt-1 font-display text-[32px] font-extrabold leading-[1.05] text-brand-blue-dark [overflow-wrap:anywhere]">
            {nome}
          </h2>
          <span className="text-sm text-brand-navy">
            {funci.nome} · <b className="text-brand-blue">{funci.matricula}</b>
          </span>
          {(funci.diretoria || funci.funcao) && (
            <span className="text-[13px] text-brand-navy">{[funci.diretoria, funci.funcao].filter(Boolean).join(' · ')}</span>
          )}
        </div>
      </section>

      {funci.galeria && (
        <SecaoDoSelo tipo="galeria" ciclo={ciclo.titulo}>
          <ListaDeReconhecimentos
            titulo={`Reconhecimentos que colocaram ${nome} em destaque`}
            reconhecimentos={reconhecimentos}
          />
        </SecaoDoSelo>
      )}

      {funci.cultura && (
        <SecaoDoSelo
          tipo="cultura"
          ciclo={ciclo.titulo}
          comportamento={funci.cultura}
          aoLadoDoTitulo={
            <span className="group/tip">
              <InfoTooltip rotulo="Regras dos Destaques da Cultura" largura="w-[min(380px,calc(100vw-72px))]">
                <RegrasDosDestaquesDaCultura />
              </InfoTooltip>
            </span>
          }
        >
          <ListaDeReconhecimentos titulo={`Reconhecimentos em ${comportamento}`} reconhecimentos={daCultura} />
        </SecaoDoSelo>
      )}

      {funci.realizacao && (
        <SecaoDoSelo tipo="realizacao" ciclo={ciclo.titulo}>
          <h3 className="m-0 font-display text-[17px] font-bold leading-snug text-brand-blue-dark">
            Realizações que deram o selo <span className="text-brand-blue">(1)</span>
          </h3>
          <article className="flex flex-col gap-3 rounded-xl border border-solid border-line px-[22px] py-[18px]">
            <div className="flex flex-wrap items-start gap-4">
              <div className="flex min-w-[220px] flex-1 flex-col gap-1">
                <span className="font-display text-base font-bold leading-[1.35] text-brand-blue-dark">
                  {funci.realizacao.titulo}
                </span>
                {!!funci.cadastro && (
                  <span className="text-xs text-content">
                    {['Cadastrada em ' + dataCurta(funci.cadastro), funci.indicador].filter(Boolean).join(' · ')}
                  </span>
                )}
              </div>
              {funci.realizacao.total !== undefined && (
                <div className="flex flex-col items-end gap-1">
                  <span className="text-[11px] text-content">Pontuação da realização</span>
                  <span className="whitespace-nowrap rounded-jj-pill bg-brand-blue px-3.5 py-1.5 font-display text-[15px] font-bold text-brand-white">
                    {FormatNumber(funci.realizacao.total, false)} pts
                  </span>
                </div>
              )}
            </div>
            {funci.realizacao.descricao && (
              <div className="flex flex-col gap-1 border-0 border-t border-solid border-line pt-3">
                <span className="font-display text-xs font-bold text-brand-blue">Detalhes da realização</span>
                <div
                  className="max-w-[80ch] text-sm leading-[1.55] text-content [overflow-wrap:anywhere]"
                  // A descrição foi digitada por quem cadastrou a realização: passa pelo sanitizador.
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(funci.realizacao.descricao) }}
                />
              </div>
            )}
          </article>
        </SecaoDoSelo>
      )}
    </div>
  );
}

function Aviso({ texto, children }: { texto: string; children: ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-3xl bg-surface-0 px-6 py-9 text-center shadow-jj-01">
      <p className="m-0 text-sm text-content">{texto}</p>
      {children}
    </div>
  );
}

/** DST-24 e DST-25: os reconhecimentos de uma seção, com os 4 primeiros e o "Ver todos". */
function ListaDeReconhecimentos({ titulo, reconhecimentos }: { titulo: string; reconhecimentos: ReconhecimentoDoExtrato[] }) {
  const [todos, setTodos] = useState(false);
  const listaId = useId();
  const visiveis = todos ? reconhecimentos : reconhecimentos.slice(0, RECONHECIMENTOS_VISIVEIS);

  return (
    <>
      <h3 className="m-0 font-display text-[17px] font-bold leading-snug text-brand-blue-dark">
        {titulo} <span className="text-brand-blue">({reconhecimentos.length})</span>
      </h3>
      {visiveis.length > 0 && (
        <ul id={listaId} className="m-0 grid list-none grid-cols-1 gap-3 p-0 md:grid-cols-2">
          {visiveis.map((reconhecimento, indice) => (
            <li key={reconhecimento._id ?? indice} className="flex min-w-0 flex-col [&>div]:flex-1">
              <ReconhecimentoRecebido reconhecimento={reconhecimento} />
            </li>
          ))}
        </ul>
      )}
      {reconhecimentos.length > RECONHECIMENTOS_VISIVEIS && (
        <Button
          variant="secundario"
          size="sm"
          className="self-center"
          aria-expanded={todos}
          aria-controls={listaId}
          onClick={() => setTodos(atual => !atual)}
        >
          {todos ? 'Mostrar menos' : `Ver todos os ${reconhecimentos.length} reconhecimentos`}
        </Button>
      )}
    </>
  );
}
